import React, { useState, useEffect } from 'react';
import { collection, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { PlanComparisonResult } from '../../../services/planComparisonService';
import { DowngradeSelections } from '../../../services/downgradeService';
import PaymentButtons from '../PaymentButtons';

interface SummaryStepProps {
  comparison: PlanComparisonResult;
  selections: DowngradeSelections;
  currentCounts: Record<string, number>;
}

export default function SummaryStep({
  comparison,
  selections,
  currentCounts,
}: SummaryStepProps) {
  const [showPayment, setShowPayment] = useState(false);
  const [selectedNames, setSelectedNames] = useState<{
    activities: string[];
    checklists: string[];
    recipes: string[];
    shoppingList: string;
  }>({
    activities: [],
    checklists: [],
    recipes: [],
    shoppingList: '',
  });
  
  // Si está mostrando el panel de pago, renderizar eso
  if (showPayment) {
    return <PaymentButtons onCancel={() => setShowPayment(false)} />;
  }
  
  // Cargar nombres de los items seleccionados
  useEffect(() => {
    loadSelectedNames();
  }, [selections]);
  
  const loadSelectedNames = async () => {
    const names: any = {
      activities: [],
      checklists: [],
      recipes: [],
      shoppingList: '',
    };
    
    // Cargar nombres de actividades
    if (selections.activeActivityIds) {
      for (const id of selections.activeActivityIds) {
        try {
          const docSnap = await getDoc(doc(db, 'activities', id));
          if (docSnap.exists()) {
            names.activities.push(docSnap.data().name || 'Sin nombre');
          }
        } catch (error) {
          console.error('Error cargando actividad:', error);
        }
      }
    }
    
    // Cargar nombres de checklists
    if (selections.activeChecklistIds) {
      for (const id of selections.activeChecklistIds) {
        try {
          const docSnap = await getDoc(doc(db, 'checklist', id));
          if (docSnap.exists()) {
            names.checklists.push(docSnap.data().name || 'Sin nombre');
          }
        } catch (error) {
          console.error('Error cargando checklist:', error);
        }
      }
    }
    
    // Cargar nombres de recetas
    if (selections.activeRecipeIds) {
      for (const id of selections.activeRecipeIds) {
        try {
          const docSnap = await getDoc(doc(db, 'recipes', id));
          if (docSnap.exists()) {
            names.recipes.push(docSnap.data().name || 'Sin nombre');
          }
        } catch (error) {
          console.error('Error cargando receta:', error);
        }
      }
    }
    
    // Cargar nombre de lista de compras
    if (selections.activeShoppingListId) {
      try {
        const docSnap = await getDoc(doc(db, 'shopping_lists', selections.activeShoppingListId));
        if (docSnap.exists()) {
          names.shoppingList = docSnap.data().name || 'Sin nombre';
        }
      } catch (error) {
        console.error('Error cargando lista:', error);
      }
    }
    
    setSelectedNames(names);
  };
  
  const { fromPlan, toPlan } = comparison;
  
  // Calcular items que se pausarán
  const pausedCounts = {
    activities: (currentCounts.activities || 0) - (selections.activeActivityIds?.length || 0),
    checklists: (currentCounts.checklists || 0) - (selections.activeChecklistIds?.length || 0),
    recipes: (currentCounts.recipes || 0) - (selections.activeRecipeIds?.length || 0),
    shoppingLists: (currentCounts.shoppingLists || 0) - (selections.activeShoppingListId ? 1 : 0),
    rewards: currentCounts.rewards || 0,
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="text-6xl mb-4">📋</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Resumen de tu Plan Gratuito
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Revisa tus selecciones antes de confirmar
        </p>
      </div>
      
      {/* Plan comparison summary */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 border-2 border-purple-200 dark:border-purple-800">
        <div className="flex items-center justify-center gap-4">
          <div className="text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Plan anterior</div>
            <div className="font-bold text-gray-900 dark:text-white">{fromPlan.name}</div>
          </div>
          <div className="text-3xl">→</div>
          <div className="text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Nuevo plan</div>
            <div className="font-bold text-gray-900 dark:text-white">{toPlan.name}</div>
          </div>
        </div>
      </div>
      
      {/* What you're keeping active */}
      <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 border-2 border-green-500">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <span>✅</span>
          <span>Mantendrás Activo:</span>
        </h3>
        
        <div className="space-y-4">
          {/* Activities */}
          {selectedNames.activities.length > 0 && (
            <div>
              <div className="font-medium text-gray-900 dark:text-white mb-2">
                🏃 {selectedNames.activities.length} Actividades:
              </div>
              <ul className="ml-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                {selectedNames.activities.map((name, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-green-500 mt-0.5">•</span>
                    <span>{name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Checklists */}
          {selectedNames.checklists.length > 0 && (
            <div>
              <div className="font-medium text-gray-900 dark:text-white mb-2">
                📋 {selectedNames.checklists.length} Checklists:
              </div>
              <ul className="ml-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                {selectedNames.checklists.map((name, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-green-500 mt-0.5">•</span>
                    <span>{name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Recipes */}
          {selectedNames.recipes.length > 0 && (
            <div>
              <div className="font-medium text-gray-900 dark:text-white mb-2">
                🍳 {selectedNames.recipes.length} Recetas:
              </div>
              <ul className="ml-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                {selectedNames.recipes.map((name, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-green-500 mt-0.5">•</span>
                    <span>{name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Shopping List */}
          {selectedNames.shoppingList && (
            <div>
              <div className="font-medium text-gray-900 dark:text-white mb-2">
                🛒 1 Lista de Compras:
              </div>
              <ul className="ml-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">•</span>
                  <span>{selectedNames.shoppingList}</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
      
      {/* What will be paused */}
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border-2 border-amber-500">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <span>⏸️</span>
          <span>Se Pausarán (recuperables):</span>
        </h3>
        
        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          {pausedCounts.activities > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-amber-600 dark:text-amber-400">•</span>
              <span>{pausedCounts.activities} Actividades</span>
            </div>
          )}
          {pausedCounts.checklists > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-amber-600 dark:text-amber-400">•</span>
              <span>{pausedCounts.checklists} Checklists</span>
            </div>
          )}
          {pausedCounts.recipes > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-amber-600 dark:text-amber-400">•</span>
              <span>{pausedCounts.recipes} Recetas</span>
            </div>
          )}
          {pausedCounts.shoppingLists > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-amber-600 dark:text-amber-400">•</span>
              <span>{pausedCounts.shoppingLists} Lista(s) de Compras</span>
            </div>
          )}
          {pausedCounts.rewards > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-amber-600 dark:text-amber-400">•</span>
              <span>{pausedCounts.rewards} Recompensas (bloqueadas en plan free)</span>
            </div>
          )}
          
          {Object.values(pausedCounts).every(c => c === 0) && (
            <div className="text-gray-500 dark:text-gray-400 italic">
              No se pausará ningún contenido
            </div>
          )}
        </div>
      </div>
      
      {/* Final message */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border-2 border-blue-200 dark:border-blue-800">
        <div className="flex gap-4">
          <div className="text-3xl flex-shrink-0">💡</div>
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <p className="font-semibold mb-2">Recordatorio importante:</p>
            <ul className="space-y-1">
              <li>✓ Todo el contenido pausado permanece guardado</li>
              <li>✓ Nada se borra, solo se pausa temporalmente</li>
              <li>✓ Al renovar tu plan, todo vuelve automáticamente</li>
              <li>✓ El historial y progreso se mantiene intacto</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* CTA to renew */}
      <div className="text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          ¿Quieres mantener todo activo?
        </p>
        <button
          onClick={() => setShowPayment(true)}
          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors inline-flex items-center gap-2"
        >
          <span>🔄</span>
          <span>Ver Planes y Renovar</span>
        </button>
      </div>
    </div>
  );
}
